import * as React from 'react';

/** How long typing must pause before a list of options narrows. */
export const OPTION_SEARCH_DEBOUNCE_MS = 250;

/**
 * Search text for filtering a list, settled after a pause in typing.
 * `pending` says the list still shows an older search; `flush` catches up
 * at once, for keys that act on the list (arrows, Enter). A new `scope` (a
 * different field being searched) starts from the typed text immediately.
 */
export function useDebouncedSearch(
  text: string,
  delay: number = OPTION_SEARCH_DEBOUNCE_MS,
  scope = '',
) {
  const [settled, setSettled] = React.useState({ scope, text });
  const value = settled.scope === scope ? settled.text : text;

  React.useEffect(() => {
    if (settled.scope === scope && settled.text === text) return;
    const id = window.setTimeout(
      () => setSettled({ scope, text }),
      settled.scope === scope ? delay : 0,
    );
    return () => window.clearTimeout(id);
  }, [delay, scope, settled, text]);

  return {
    value,
    pending: value !== text,
    flush: () => setSettled({ scope, text }),
  };
}
